// supabase/functions/_shared/preorder.ts
//
// The half-time pre-order window rule. Its SQL twin is
// public.preorder_window_closed in
// supabase/migrations/20260922150000_half_time_preorders.sql — both must
// agree term for term (see the migration's own comment on the COALESCE of a
// missing match_status). This file is the single source of truth for the
// window/eligibility rule on both sides: the live-score trigger and cron
// use the SQL function directly, while the fan page and the Orders hub use
// this module (via src/lib/preorder.ts re-exporting it for the browser).
//
// It also holds the "Pick-up now" club switch rule (isPickupNowEnabled /
// pickupAvailability), shared by create-payment-intent and the fan page.
//
// Pure and dependency-free: imported by the Deno edge functions and by the
// browser, unit-tested under Node (this file has no imports at all).

export const PREORDER_SLOT = 'half_time' as const;
export type PickupSlot = typeof PREORDER_SLOT;

/** First-half minute at which the window closes when a club has not set its own cutoff. */
export const DEFAULT_PREORDER_CUTOFF_MINUTE = 35;
/** Absolute release ceiling: every scheduled pre-order releases by kickoff + this many minutes, regardless of the live feed. */
export const PREORDER_HARD_CAP_MIN = 60;
/** Pre-orders never open earlier than this many minutes before kickoff. */
export const PREORDER_OPENS_BEFORE_KICKOFF_MIN = 180;
/** Cutoff minutes a club may pick for club_settings.preorder_cutoff_minute. */
export const PREORDER_CUTOFF_OPTIONS = [25, 30, 35, 40] as const;

export interface PreorderWindowInput {
  /** event_live_scores.match_status; null/undefined = no live-score row. */
  matchStatus: string | null | undefined;
  liveMinute: number | null | undefined;
  kickoffIso: string | null | undefined;
  /** null/undefined → DEFAULT_PREORDER_CUTOFF_MINUTE */
  cutoffMinute: number | null | undefined;
  nowMs?: number; // default Date.now()
}

/**
 * Sticky: the Global Constraints rule, term for term (mirrors
 * public.preorder_window_closed):
 *   - kickoff missing/unparseable → closed
 *   - match_status ∈ {half_time, finished, postponed, cancelled} → closed
 *     (a missing match_status is treated as '', never matching this set)
 *   - match_status = 'live' and minute (missing → 0) >= cutoff → closed
 *   - minute missing and now > kickoff + cutoff minutes → closed
 *     (status-independent: covers events with no live-score row at all)
 *   - now > kickoff + PREORDER_HARD_CAP_MIN minutes → closed, always
 */
export function isPreorderWindowClosed(i: PreorderWindowInput): boolean {
  const ms = i.kickoffIso ? new Date(i.kickoffIso).getTime() : NaN;
  if (Number.isNaN(ms)) return true;

  const cutoff = i.cutoffMinute ?? DEFAULT_PREORDER_CUTOFF_MINUTE;
  const now = i.nowMs ?? Date.now();
  const status = i.matchStatus ?? '';
  const minute = i.liveMinute;

  if (status === 'half_time' || status === 'finished' || status === 'postponed' || status === 'cancelled') return true;
  if (status === 'live' && (minute ?? 0) >= cutoff) return true;
  if ((minute === null || minute === undefined) && now > ms + cutoff * 60_000) return true;
  if (now > ms + PREORDER_HARD_CAP_MIN * 60_000) return true;
  return false;
}

export interface PreorderOfferInput extends PreorderWindowInput {
  preorderEnabled: boolean | null | undefined;
  /** section_ordering_configs.ordering_mode: 'delivery' | 'pickup' | 'both' */
  sectionMode: string | null | undefined;
  hasCollectionPoint: boolean;
  /** club_settings.payment_mode: 'online_only' | 'in_person_only' | 'fan_choice' */
  clubPaymentMode: string | null | undefined;
  /** get_club_online_payment_status result; must be exactly true */
  onlinePaymentReady: boolean | null | undefined;
  /** events.event_kind; null/undefined counts as 'sports_match' */
  eventKind: string | null | undefined;
}

/**
 * enabled ∧ sectionMode ∈ {pickup, both} ∧ hasCollectionPoint ∧
 * clubPaymentMode !== 'in_person_only' ∧ onlinePaymentReady === true ∧
 * (eventKind ?? 'sports_match') === 'sports_match' ∧ kickoff parseable ∧
 * now >= kickoff − PREORDER_OPENS_BEFORE_KICKOFF_MIN min ∧
 * !isPreorderWindowClosed(i)
 */
export function canOfferPreorder(i: PreorderOfferInput): boolean {
  if (!i.preorderEnabled) return false;
  if (i.sectionMode !== 'pickup' && i.sectionMode !== 'both') return false;
  if (!i.hasCollectionPoint) return false;
  if (i.clubPaymentMode === 'in_person_only') return false;
  if (i.onlinePaymentReady !== true) return false;
  if ((i.eventKind ?? 'sports_match') !== 'sports_match') return false;

  const ms = i.kickoffIso ? new Date(i.kickoffIso).getTime() : NaN;
  if (Number.isNaN(ms)) return false;

  const now = i.nowMs ?? Date.now();
  if (now < ms - PREORDER_OPENS_BEFORE_KICKOFF_MIN * 60_000) return false;

  return !isPreorderWindowClosed(i);
}

/** kickoff + cutoff minutes, or null when kickoff is missing/unparseable. */
export function preorderClosesAtMs(
  kickoffIso: string | null | undefined,
  cutoffMinute: number | null | undefined,
): number | null {
  const ms = kickoffIso ? new Date(kickoffIso).getTime() : NaN;
  if (Number.isNaN(ms)) return null;
  return ms + (cutoffMinute ?? DEFAULT_PREORDER_CUTOFF_MINUTE) * 60_000;
}

/** kickoff + PREORDER_HARD_CAP_MIN minutes, or null. */
export function preorderHardCapAtMs(kickoffIso: string | null | undefined): number | null {
  const ms = kickoffIso ? new Date(kickoffIso).getTime() : NaN;
  if (Number.isNaN(ms)) return null;
  return ms + PREORDER_HARD_CAP_MIN * 60_000;
}

/**
 * club_settings.pickup_now_enabled: whether fans may place a normal (now)
 * pick-up order. Only an explicit false disables it — null/undefined (the
 * column not migrated yet, or no settings row) means enabled, matching the
 * column's DEFAULT true. create-payment-intent enforces it (409
 * pickup_now_disabled); the fan page uses it to shape the pick-up choices.
 */
export function isPickupNowEnabled(flag: boolean | null | undefined): boolean {
  return flag !== false;
}

export interface PickupAvailabilityInput {
  /** isPickupNowEnabled(club_settings.pickup_now_enabled) */
  pickupNowEnabled: boolean;
  /** canOfferPreorder(...) for this section/match right now */
  preorderOfferable: boolean;
}

/** Which pick-up options a fan can use right now: now, half-time, and whether any pick-up exists at all. */
export function pickupAvailability(
  i: PickupAvailabilityInput,
): { now: boolean; halfTime: boolean; any: boolean } {
  const now = i.pickupNowEnabled;
  const halfTime = i.preorderOfferable;
  return { now, halfTime, any: now || halfTime };
}

/** Local-storage key for a fan's pending pre-order on a given NFC tag. */
export function preorderStorageKey(tagId: string): string {
  return `idseat_preorder_${tagId}`;
}

export interface AggregatedLine {
  label: string;
  quantity: number;
}

/**
 * Sums quantities per item across orders (a kitchen prep list, not a
 * per-order receipt). label = item_name + (size ? ` (${size})` : '');
 * sorted by quantity desc, then label asc. Empty input → [].
 */
export function aggregateItemLines(
  orders: ReadonlyArray<{ items: ReadonlyArray<{ item_name: string; item_size?: string | null; quantity: number }> }>,
): AggregatedLine[] {
  const totals = new Map<string, number>();
  for (const order of orders) {
    for (const item of order.items) {
      const label = item.item_size ? `${item.item_name} (${item.item_size})` : item.item_name;
      totals.set(label, (totals.get(label) ?? 0) + item.quantity);
    }
  }
  return [...totals.entries()]
    .map(([label, quantity]) => ({ label, quantity }))
    .sort((a, b) => b.quantity - a.quantity || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
}
